import { AuthContext } from "@/context/AuthContext";
import ConductionAPI from "@/javascript/conduction-api";
import { Classroom } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";
import { Box, Button, Flex, Stack, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { useContext } from "react";
import ConductionModal from "../ConductionModal";

interface AdminClassroomDuplicatorProps {
	classroom: Classroom;
	onClose: () => void;
	onDuplicated: () => void;
}

const AdminClassroomDuplicator: React.FC<AdminClassroomDuplicatorProps> = ({ classroom, onClose, onDuplicated }) => {
	const auth = useContext(AuthContext);
	if (!auth) {
		throw new Error("AdminClassroomDuplcator must be a child of AuthContext.");
	}
	const token = auth.token;
	if (token === null) {
		return null;
	}

	// Set up the form with initial values and validations.
	const form = useForm({
		initialValues: {
			name: ""
		},
		validate: {
			name: (value) => (value.trim().length === 0 ? "Classroom name is required" : null)
		},
	});

	const handleSubmit = async () => {
		// Validate the form and display errors if necessary.
		if (!form.isValid()) {
			form.validate();
			return;
		}

		const notificationId = Utils.displayNotification(
			"Creating Classroom...",
			"Your new classroom is being created.",
			"blue",
			"loading"
		);

		const name = form.values.name;

		try {
			await ConductionAPI.duplicateClassroom(classroom.id, name, token);
			Utils.updateNotification(
				notificationId,
				`${name} Classroom Duplicated`,
				undefined,
				"green",
				<IconCheck size={16} />
			);
			onDuplicated();
			onClose();
		} catch (error) {
			console.error("Error creating classroom", error);
			Utils.updateNotification(
				notificationId,
				"Error",
				"Failed to create classroom.",
				"red",
				<IconAlertCircle size={16} />,
				true
			);
		}
	};

	const height = 29; // Use 40vh for view height as in the AdminSongCreator

	return (
		<ConductionModal
			title="Duplicate Classroom"
			onClose={onClose}
			size="md"
		>
			<Box style={{ minHeight: `${height}vh` }}>
				<Stack>
					<Text>
						Duplicate the {classroom?.name ?? "Error"} classroom?
					</Text>
					<form onSubmit={form.onSubmit(handleSubmit)}>
						<Flex
							direction="column"
							justify="space-between"
							align="stretch"
							style={{ minHeight: `${height}vh` }}
							onKeyDown={(e) => e.stopPropagation()} // Prevent hotkey conflicts
							onKeyUp={(e) => e.stopPropagation()}
						>
							<Stack>
								<TextInput
									label="New Classroom Name"
									{...form.getInputProps("name")}
									required
								/>
							</Stack>

							<Button
								type="submit"
								radius="xl"
								size="sm"
								loading={form.submitting}
								color="red"
							>
								Duplicate Classroom
							</Button>
						</Flex>
					</form>
				</Stack>
			</Box>
		</ConductionModal>
	);
};

export default AdminClassroomDuplicator;
